import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import { formatMoney } from '../utils/money';

const TEXT = {
  es: {
    orders: 'Pedidos este mes',
    sales: 'Ventas este mes',
    profit: 'Ganancia real este mes',
    vsPrev: 'vs mes anterior',
    noPrev: 'Sin datos del mes anterior',
  },
  en: {
    orders: 'Orders this month',
    sales: 'Sales this month',
    profit: 'Real profit this month',
    vsPrev: 'vs last month',
    noPrev: 'No data for last month',
  },
  fr: {
    orders: 'Commandes ce mois-ci',
    sales: 'Ventes ce mois-ci',
    profit: 'B\u00e9n\u00e9fice r\u00e9el ce mois-ci',
    vsPrev: 'vs mois pr\u00e9c\u00e9dent',
    noPrev: 'Pas de donn\u00e9es le mois pr\u00e9c\u00e9dent',
  },
};

const getText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

const monthKey = (offset) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const change = (current, previous) => {
  if (previous === null || previous === undefined || Number(previous) === 0) return null;
  return (Number(current) - Number(previous)) / Math.abs(Number(previous));
};

function DashboardStats({ token, onAuthError }) {
  const { i18n } = useTranslation();
  const tx = getText(i18n.language);
  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API_URL}/api/v1/profit-summary?month=${monthKey(0)}`, headers),
      axios.get(`${API_URL}/api/v1/profit-summary?month=${monthKey(-1)}`, headers),
    ])
      .then(([cur, prev]) => {
        setCurrent(cur.data.totals);
        setCurrency(cur.data.currency || 'EUR');
        setPrevious(prev.data.totals);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) onAuthError();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const money = (value) =>
    formatMoney(value, i18n.language, currency);
  const percent = (value) =>
    new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(value);

  const cards = [
    {
      label: tx.orders,
      icon: '\u25C8',
      value: current ? String(current.orders) : '\u2014',
      delta: current && previous ? change(current.orders, previous.orders) : null,
      negative: false,
    },
    {
      label: tx.sales,
      icon: '\u25C9',
      value: current ? money(current.gross) : '\u2014',
      delta: current && previous ? change(current.gross, previous.gross) : null,
      negative: false,
    },
    {
      label: tx.profit,
      icon: '\u26A1',
      value: current ? money(current.real_profit) : '\u2014',
      // Un porcentaje sobre una ganancia anterior negativa o cero no tiene sentido: se muestra la diferencia en dinero.
      ...(() => {
        if (!current || !previous) return { delta: null };
        const prev = Number(previous.real_profit) || 0;
        if (prev > 0) return { delta: change(current.real_profit, prev) };
        if (prev === 0 && !previous.orders) return { delta: null };
        return { delta: null, deltaMoney: (Number(current.real_profit) || 0) - prev };
      })(),
      negative: !!current && current.real_profit < 0,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
      {cards.map((card) => (
        <div key={card.label} className="bg-surface border border-edge rounded-2xl p-6 min-w-0">
          <div className="flex justify-between items-start gap-2 mb-4">
            <p className="text-muted text-[13px]">{card.label}</p>
            <div className="bg-surfaceAlt rounded-lg px-2.5 py-1.5 text-accent text-base shrink-0">{card.icon}</div>
          </div>
          <p className={`text-2xl lg:text-3xl font-bold tracking-tight break-words ${card.negative ? 'text-red-400' : 'text-white'}`}>
            {card.value}
          </p>
          <div className="mt-3 text-xs">
            {card.deltaMoney !== undefined ? (
              <>
                <span className={`font-semibold ${card.deltaMoney >= 0 ? 'text-accent' : 'text-red-400'}`}>
                  {card.deltaMoney > 0 ? '+' : ''}{money(card.deltaMoney)}
                </span>
                <span className="text-muted"> {tx.vsPrev}</span>
              </>
            ) : card.delta === null ? (
              <span className="text-muted">{tx.noPrev}</span>
            ) : (
              <>
                <span className={`font-semibold ${card.delta >= 0 ? 'text-accent' : 'text-red-400'}`}>{percent(card.delta)}</span>
                <span className="text-muted"> {tx.vsPrev}</span>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default DashboardStats;